import { uiText } from '@/i18n/interface';
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getAuthRedirectURL } from "@/utils/getURL";

export const useLoginState = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, profile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [resetMessage, setResetMessage] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Check for email confirmation message and code
  // Handle both URL search params and hash-based params for Supabase redirects
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '');

  const checkEmailMessage = searchParams.get("message") === "check-email" || urlParams.get("message") === "check-email" || hashParams.get("message") === "check-email";
  const emailConfirmedMessage = searchParams.get("message") === "email-confirmed" || urlParams.get("message") === "email-confirmed" || hashParams.get("message") === "email-confirmed";
  const confirmationCode = searchParams.get("code") || urlParams.get("code") || hashParams.get("code");

  useEffect(() => {
    console.log("useLoginState - Auth state changed:", {
      isAuthenticated,
      profile: profile
        ? {
            email: profile.email,
            role: profile.role,
            id: profile.id,
          }
        : null,
    });

    // Handle email confirmation code
    if (confirmationCode && !isAuthenticated) {
      console.log("Processing email confirmation code:", confirmationCode);
      // The confirmation will be handled by Supabase automatically
      // Show a message and redirect to main URL
      toast({
        title: uiText("Email Confirmation"),
        description: uiText("Email confirmed successfully! Redirecting..."),
      });

      // Redirect to main URL after confirmation
      setTimeout(() => {
        window.location.href = getURL();
      }, 2000);
    }

    if (isAuthenticated && profile) {
      console.log(
        "User already authenticated, redirecting based on role:",
        profile.role,
      );
      const getRedirectPath = (role: string) => {
        if (role === "super_user") return "/super-user";
        if (role === "admin") return "/admin";
        return "/student";
      };
      const redirectPath = getRedirectPath(profile.role);
      console.log("Redirecting to:", redirectPath);
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, profile, navigate]);

  // Reset lockout after 15 minutes
  useEffect(() => {
    if (isLocked) {
      const timer = setTimeout(
        () => {
          setIsLocked(false);
          setLoginAttempts(0);
        },
        15 * 60 * 1000,
      ); // 15 minutes

      return () => clearTimeout(timer);
    }
  }, [isLocked]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLocked) {
      toast({
        title: uiText("Account Temporarily Locked"),
        description:
          uiText("Too many failed login attempts. Please try again in 15 minutes."),
        variant: "destructive",
      });
      return;
    }

    if (!formData.email || !formData.password) {
      toast({
        title: uiText("Missing Information"),
        description: uiText("Please enter both email and password."),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      console.log("Attempting login for:", formData.email);
      const result = await login(formData.email, formData.password);

      if (!result.success) {
        console.error("Login failed:", result.error);

        // Increment failed attempts
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);

        // Lock account after 5 failed attempts
        if (newAttempts >= 5) {
          setIsLocked(true);
          toast({
            title: uiText("Account Locked"),
            description:
              uiText("Too many failed login attempts. Your account has been temporarily locked for 15 minutes."),
            variant: "destructive",
            duration: 10000,
          });
          return;
        }

        // Provide more specific error messages
        if (result.error?.includes("Email not confirmed")) {
          toast({
            title: uiText("Email Not Confirmed"),
            description:
              uiText("Please check your email and click the confirmation link before logging in. Check your spam folder if you don't see it."),
            variant: "destructive",
            duration: 8000,
          });
        } else if (result.error?.includes("Invalid login credentials")) {
          toast({
            title: uiText("Login Failed"),
            description: uiText("Invalid email or password. {{value0}} attempts remaining before temporary lockout.", { value0: 5 - newAttempts }),
            variant: "destructive",
          });
        } else {
          toast({
            title: uiText("Login Failed"),
            description:
              result.error ||
              "An error occurred during login. Please try again.",
            variant: "destructive",
          });
        }
        return;
      }

      // Reset attempts on successful login
      setLoginAttempts(0);
      setIsLocked(false);

      console.log("Login successful");
      toast({
        title: uiText("Login Successful"),
        description: uiText("Welcome back to the Dojo!"),
      });
    } catch (error) {
      console.error("Unexpected login error:", error);
      toast({
        title: uiText("Login Error"),
        description: uiText("An unexpected error occurred. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePasswordReset = async () => {
    if (!formData.email) {
      toast({
        title: uiText("Missing Information"),
        description: uiText("Please enter your email address to reset your password."),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setResetMessage("");

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: getAuthRedirectURL('auth/reset-password')
      });

      if (error) {
        console.error('Password reset error:', error);
        setResetMessage(`Error: ${error.message}`);
        setResetEmailSent(false);
        toast({
          title: uiText("Reset Failed"),
          description: error.message,
          variant: "destructive",
        });
      } else {
        setResetMessage('Password reset email sent. Please check your inbox (and spam folder).');
        setResetEmailSent(true);
        toast({
          title: uiText("Reset Email Sent"),
          description: uiText("Please check your email for password reset instructions."),
        });
      }
    } catch (error) {
      console.error('Unexpected password reset error:', error);
      setResetMessage('An unexpected error occurred. Please try again.');
      setResetEmailSent(false);
      toast({
        title: uiText("Reset Error"),
        description: uiText("An unexpected error occurred. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    showPassword,
    setShowPassword,
    isLoading,
    isLocked,
    loginAttempts,
    checkEmailMessage,
    emailConfirmedMessage,
    confirmationCode,
    resetMessage,
    resetEmailSent,
    handleSubmit,
    handleInputChange,
    handlePasswordReset,
  };
};
